import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

const HeaderBar = styled.header`
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.95);
`;

const HeaderInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  font-size: 1.25rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: -0.02em;
`;

const DesktopNav = styled.nav`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ $active: boolean }>`
  padding: 0.45rem 0.9rem;
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primaryLight : "transparent"};
  transition: all 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const CartLink = styled(Link)<{ $active: boolean }>`
  position: relative;
  padding: 0.45rem 0.9rem;
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primaryLight : "transparent"};
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: ${({ theme }) => theme.colors.error};
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.radii.full};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const UserArea = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0.75rem;
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  margin-left: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none; /* hide on very small screens, accessible via mobile menu */
  }
`;

const UserName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const AuthButton = styled.button<{ $variant?: "logout" }>`
  padding: 0.38rem 0.8rem;
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.82rem;
  font-weight: 600;
  transition: all 0.2s;
  background: ${({ $variant, theme }) =>
    $variant === "logout" ? "transparent" : theme.colors.primary};
  color: ${({ $variant, theme }) =>
    $variant === "logout" ? theme.colors.textSecondary : "white"};
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === "logout" ? theme.colors.border : "transparent"};

  &:hover {
    background: ${({ $variant, theme }) =>
      $variant === "logout"
        ? theme.colors.background
        : theme.colors.primaryHover};
    border-color: ${({ $variant, theme }) =>
      $variant === "logout" ? theme.colors.borderHover : "transparent"};
  }
`;

/* Mobile menu */
const MobileToggler = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  background: transparent;
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid transparent;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenu = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 64px;
  right: 1rem;
  width: calc(100% - 2rem);
  max-width: 360px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transform-origin: top right;
  transition: opacity 0.18s ease, transform 0.18s ease;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transform: ${({ $open }) => ($open ? "scale(1)" : "scale(0.98)")};
  overflow: hidden;
  z-index: 120;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
`;

const MobileItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: ${({ theme }) => theme.radii.md};
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
`;

const MobileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: ${({ theme }) => theme.radii.md};
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  width: 100%;
  text-align: left;
  font-size: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
`;

const MobileUserInfo = styled.div`
  padding: 0.45rem 0.75rem;
`;

const MobileUserName = styled.div`
  font-weight: 700;
`;

const MobileUserEmail = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const MobileCartCount = styled.span`
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 700;
`;

const MobileDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0.4rem 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state: authState, logout } = useAuth();
  const { state: cartState } = useCart();

  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <HeaderBar>
      <HeaderInner>
        <Logo to="/">🛍️ FakeStore</Logo>

        <DesktopNav>
          <NavLink to="/" $active={location.pathname === "/"}>
            Products
          </NavLink>

          {authState.isAuthenticated && (
            <NavLink to="/create" $active={location.pathname === "/create"}>
              Add Product
            </NavLink>
          )}

          <CartLink to="/cart" $active={location.pathname === "/cart"}>
            🛒 Cart
            {cartState.totalItems > 0 && (
              <CartBadge>{cartState.totalItems}</CartBadge>
            )}
          </CartLink>
        </DesktopNav>

        <RightSection>
          <UserArea>
            {authState.isAuthenticated && authState.user ? (
              <>
                <UserName>
                  {authState.user.name.firstname} {authState.user.name.lastname}
                </UserName>
                <AuthButton $variant="logout" onClick={handleLogout}>
                  Logout
                </AuthButton>
              </>
            ) : (
              <AuthButton onClick={() => navigate("/login")}>
                Sign In
              </AuthButton>
            )}
          </UserArea>

          <MobileToggler
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((s) => !s)}
          >
            {/* simple hamburger icon */}
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 7h16"
                stroke="#374151"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 12h16"
                stroke="#374151"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 17h16"
                stroke="#374151"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </MobileToggler>
        </RightSection>

        <MobileMenu $open={open} role="menu" aria-hidden={!open}>
          <MobileMenuList>
            <MobileItem to="/" onClick={() => setOpen(false)}>
              Products
            </MobileItem>

            {authState.isAuthenticated && (
              <MobileItem to="/create" onClick={() => setOpen(false)}>
                Add Product
              </MobileItem>
            )}

            <MobileItem to="/cart" onClick={() => setOpen(false)}>
              🛒 Cart
              {cartState.totalItems > 0 && (
                <MobileCartCount>({cartState.totalItems})</MobileCartCount>
              )}
            </MobileItem>

            <MobileDivider />

            {authState.isAuthenticated && authState.user ? (
              <>
                <MobileUserInfo>
                  <MobileUserName>
                    {authState.user.name.firstname}{" "}
                    {authState.user.name.lastname}
                  </MobileUserName>
                  <MobileUserEmail>{authState.user.email}</MobileUserEmail>
                </MobileUserInfo>
                <MobileButton onClick={handleLogout}>Logout</MobileButton>
              </>
            ) : (
              <MobileItem to="/login" onClick={() => setOpen(false)}>
                Sign In
              </MobileItem>
            )}
          </MobileMenuList>
        </MobileMenu>
      </HeaderInner>
    </HeaderBar>
  );
};

export default Header;
